import { Outlet, Navigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import useAdminT from '../hooks/useAdminT';
import {
  LayoutDashboard,
  Users,
  FileText,
  Receipt,
  UserPlus,
  FolderOpen,
  Settings,
  LogOut,
  Menu,
  X,
  HardHat,
  Clock,
  Hammer,
  Newspaper,
  TrendingUp,
  HelpCircle,
  CheckSquare,
  TableProperties,
  CalendarDays,
} from 'lucide-react';
import { useState } from 'react';
import { TourProvider, useTour } from '../context/TourContext';
import AdminTour from '../components/admin/AdminTour';

// Admin only supports EN (default), FR, DE
const ADMIN_LANGS = { en: '🇬🇧 EN', fr: '🇫🇷 FR', de: '🇩🇪 DE' };

/**
 * Permanent help button that lives in the top bar and launches the
 * currently-registered tour. Hidden if no tour is registered for the page.
 */
const HelpButton = () => {
  const { hasTour, launch } = useTour();
  const aT = useAdminT();
  if (!hasTour) return null;
  return (
    <button
      onClick={launch}
      title={aT.tour?.helpTitle || 'Show tutorial'}
      className="
        flex items-center gap-1.5
        bg-[#E87722]/10 hover:bg-[#E87722] hover:text-white
        text-[#E87722] text-xs font-semibold uppercase tracking-wider
        px-3 py-2 rounded-lg
        transition-colors duration-200
      "
      aria-label={aT.tour?.helpTitle || 'Show tutorial'}
    >
      <HelpCircle className="w-4 h-4" />
      <span className="hidden sm:inline">{aT.tour?.help || 'Tutorial'}</span>
    </button>
  );
};

const AdminLayoutInner = () => {
  const { user, loading, logout } = useAuth();
  const { adminLanguage, setAdminLanguage } = useLanguage();
  const aT = useAdminT();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#1B4332]/20 border-t-[#1B4332] rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const menuItems = [
    { path: '/admin', icon: LayoutDashboard, label: aT.sidebar.dashboard, exact: true, tourKey: 'dashboard' },
    { path: '/admin/clients', icon: Users, label: aT.sidebar.clients, tourKey: 'clients' },
    { path: '/admin/chantiers', icon: Hammer, label: aT.sidebar.worksites, tourKey: 'worksites' },
    { path: '/admin/quotes', icon: FileText, label: aT.sidebar.quotes, tourKey: 'quotes' },
    { path: '/admin/invoices', icon: Receipt, label: aT.sidebar.invoices, tourKey: 'invoices' },
    { path: '/admin/leads', icon: UserPlus, label: aT.sidebar.leads, tourKey: 'leads' },
    { path: '/admin/projects', icon: FolderOpen, label: aT.sidebar.projects, tourKey: 'projects' },
    { path: '/admin/news', icon: Newspaper, label: aT.sidebar.news, tourKey: 'news' },
    { path: '/admin/seo', icon: TrendingUp, label: aT.sidebar.seo, tourKey: 'seo' },
    { path: '/admin/employees', icon: HardHat, label: aT.sidebar.employees, tourKey: 'employees' },
    { path: '/admin/timesheets', icon: Clock, label: aT.sidebar.timesheets, tourKey: 'timesheets' },
    { path: '/admin/planning', icon: CalendarDays, label: aT.sidebar.planning, tourKey: 'planning' },
    { path: '/admin/todos', icon: CheckSquare, label: aT.sidebar.todos, tourKey: 'todos' },
    { path: '/admin/tableur', icon: TableProperties, label: aT.sidebar.spreadsheet, tourKey: 'spreadsheet' },
    { path: '/admin/settings', icon: Settings, label: aT.sidebar.settings, tourKey: 'settings' },
  ];

  const isActive = (path, exact = false) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-layout">
      {/* Sidebar - Dark green theme */}
      <aside
        className={`fixed top-0 left-0 h-full w-64 bg-[#1B4332] z-50 transform transition-transform duration-300 shadow-xl ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
        data-testid="admin-sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Logo Remalex — fond vert intégré, pas de wrapper blanc */}
          <div className="px-4 py-5 border-b border-white/10 flex items-center justify-center">
            <img src="/logo-remalex.png" alt="Construcciones Remalex" className="h-14 w-auto" />
          </div>

          {/* Menu */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path, item.exact);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
                    active
                      ? 'bg-white text-[#1B4332] shadow-md'
                      : 'text-white/80 hover:bg-white/10 hover:text-white'
                  }`}
                  data-testid={`menu-${item.label.toLowerCase()}`}
                  data-tour={`sidebar-${item.tourKey}`}
                >
                  <Icon size={20} />
                  <span className="font-body text-sm font-medium">{item.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* Admin Language Selector */}
          <div className="px-4 py-3 border-t border-white/10" data-tour="lang-switch">
            <p className="text-white/40 text-xs uppercase tracking-widest mb-2 px-1">Language</p>
            <div className="flex gap-1 flex-wrap">
              {Object.entries(ADMIN_LANGS).map(([lang, label]) => (
                <button
                  key={lang}
                  onClick={() => setAdminLanguage(lang)}
                  className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${adminLanguage === lang ? 'bg-white text-[#1B4332]' : 'text-white/50 hover:text-white hover:bg-white/10'}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Logout */}
          <div className="p-4 border-t border-white/10">
            <button
              onClick={logout}
              className="flex items-center space-x-3 px-4 py-3 rounded-lg text-white/70 hover:bg-white/10 hover:text-white transition-all w-full"
              data-testid="logout-button"
            >
              <LogOut size={20} />
              <span className="font-body text-sm">{aT.sidebar.logout}</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:ml-64 min-h-screen">
        {/* Top bar — always visible (desktop + mobile), with Help button */}
        <div className="bg-white border-b border-gray-200 px-4 lg:px-8 py-3 flex items-center justify-between shadow-sm">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="text-gray-700 lg:hidden"
              data-testid="mobile-menu-toggle"
            >
              {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
            <span className="text-gray-900 font-heading text-lg lg:hidden">Remalex Admin</span>
          </div>
          <div className="flex items-center gap-2" data-tour="help-button">
            <HelpButton />
          </div>
        </div>

        {/* Content */}
        <main className="p-6 lg:p-8">
          <Outlet />
        </main>
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          data-testid="sidebar-overlay"
        />
      )}

      {/* Tour overlay (always mounted, reads from TourContext) */}
      <AdminTour />
    </div>
  );
};

const AdminLayout = () => (
  <TourProvider>
    <AdminLayoutInner />
  </TourProvider>
);

export default AdminLayout;
